import React, { useEffect, useState, useMemo } from "react";
import dashboardService from "../api/dashboardService";

const MAX_VISIBLE_COLUMNS = 10;

const TYPE_COLORS = [
  "#6366f1","#0ea5e9","#10b981","#f59e0b",
  "#ef4444","#8b5cf6","#ec4899","#14b8a6",
  "#f97316","#84cc16",
];

const MONTHS = [
  "Enero","Febrero","Marzo","Abril","Mayo","Junio",
  "Julio","Agosto","Septiembre","Octubre","Noviembre","Diciembre"
];
const YEARS = [2026, 2025, 2024];

function hexToRgb(hex) {
  const r = parseInt(hex.slice(1,3),16);
  const g = parseInt(hex.slice(3,5),16);
  const b = parseInt(hex.slice(5,7),16);
  return `${r},${g},${b}`;
}

function calcWorkload(tech, allData, typeTotals, totalGlobal) {
  const total = tech.totalServices || 0;
  if (total === 0 || allData.length === 0) return 0;

  const allTotals = allData.map(t => t.totalServices || 0).sort((a, b) => a - b);
  const n = allTotals.length;

  const below = allTotals.filter(v => v < total).length;
  const equal = allTotals.filter(v => v === total).length;
  const pRank = (below + equal * 0.5) / n;

  const base = pRank * 90;

  const median = allTotals[Math.floor(n / 2)] || 1;

  const counts = (tech.servicesByType || []).map(s => s.count || 0).filter(c => c > 0);
  const k = counts.length;
  let fragBonus = 0;
  if (k > 1 && total >= median) {
    const shannon = -counts.reduce((s, c) => s + (c/total) * Math.log(c/total), 0);
    const shannonNorm = shannon / Math.log(k);
    const kTeam = typeTotals.size;
    const coverageRatio = kTeam > 0 ? k / kTeam : 0;
    fragBonus = shannonNorm * coverageRatio * 6;
  }

  let pressureBonus = 0;
  if (totalGlobal > 0) {
    (tech.servicesByType || []).forEach(s => {
      const c = s.count || 0;
      if (!c) return;
      const typeTotal = typeTotals.get(String(s.type)) || 1;
      const typeWeight = typeTotal / totalGlobal;
      const share = c / typeTotal;
      pressureBonus += share * typeWeight;
    });
    pressureBonus = Math.min(pressureBonus * 4, 4);
  }

  return Math.min(100, Math.max(0, Math.round(base + fragBonus + pressureBonus)));
}

function workloadMeta(score) {
  if (score >= 75) return { fill: "#ef4444", track: "rgba(239,68,68,0.1)",  label: "Alta",   text: "#dc2626", dot: "#ef4444" };
  if (score >= 50) return { fill: "#f97316", track: "rgba(249,115,22,0.1)", label: "Media",  text: "#ea580c", dot: "#f97316" };
  if (score >= 28) return { fill: "#eab308", track: "rgba(234,179,8,0.1)",  label: "Normal", text: "#ca8a04", dot: "#eab308" };
  return              { fill: "#22c55e", track: "rgba(34,197,94,0.1)",  label: "Baja",   text: "#16a34a", dot: "#22c55e" };
}

const LEGEND_ITEMS = [
  { fill:"#22c55e", track:"rgba(34,197,94,0.12)",  text:"#16a34a", label:"Baja",   range:"< 28" },
  { fill:"#eab308", track:"rgba(234,179,8,0.12)",  text:"#ca8a04", label:"Normal", range:"28–49" },
  { fill:"#f97316", track:"rgba(249,115,22,0.12)", text:"#ea580c", label:"Media",  range:"50–74" },
  { fill:"#ef4444", track:"rgba(239,68,68,0.12)",  text:"#dc2626", label:"Alta",   range:"≥ 75" },
];

const GLOBAL_STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600&family=DM+Mono:wght@400;500&display=swap');

  .sbt-root {
    --font: 'DM Sans', system-ui, sans-serif;
    --font-mono: 'DM Mono', monospace;
    --radius-sm: 6px;
    --radius-md: 10px;
    --radius-lg: 14px;
    --border: #e4e7ed;
    --border-light: #f0f2f6;
    --bg: #ffffff;
    --bg-soft: #f8f9fc;
    --bg-muted: #f2f4f8;
    --text-primary: #0f1117;
    --text-secondary: #5a6175;
    --text-tertiary: #9ba3b5;
    --shadow-xs: 0 1px 2px rgba(15,17,23,0.04);
    --shadow-sm: 0 2px 8px rgba(15,17,23,0.06), 0 1px 2px rgba(15,17,23,0.04);
    font-family: var(--font);
  }

  /* ── Custom scrollbar ── */
  .sbt-scroll::-webkit-scrollbar {
    width: 6px;
    height: 6px;
  }
  .sbt-scroll::-webkit-scrollbar-track {
    background: transparent;
    margin: 4px;
  }
  .sbt-scroll::-webkit-scrollbar-thumb {
    background: #d1d5e0;
    border-radius: 100px;
    transition: background 0.2s;
  }
  .sbt-scroll::-webkit-scrollbar-thumb:hover {
    background: #9ba3b5;
  }
  .sbt-scroll::-webkit-scrollbar-corner {
    background: transparent;
  }
  .sbt-scroll {
    scrollbar-width: thin;
    scrollbar-color: #d1d5e0 transparent;
  }

  /* ── Selects ── */
  .sbt-select {
    appearance: none;
    -webkit-appearance: none;
    background: var(--bg);
    border: 1.5px solid var(--border);
    border-radius: var(--radius-md);
    color: var(--text-primary);
    font-size: 13px;
    font-weight: 500;
    font-family: var(--font);
    padding: 7px 30px 7px 12px;
    cursor: pointer;
    outline: none;
    box-shadow: var(--shadow-xs);
    transition: border-color 0.15s, box-shadow 0.15s;
    line-height: 1.4;
  }
  .sbt-select:hover {
    border-color: #c4c9d8;
    box-shadow: var(--shadow-sm);
  }
  .sbt-select:focus {
    border-color: #6366f1;
    box-shadow: 0 0 0 3px rgba(99,102,241,0.12);
  }

  /* ── Table rows ── */
  .sbt-row {
    transition: background 0.12s;
  }
  .sbt-row:hover td {
    background: rgba(99,102,241,0.025) !important;
  }
  .sbt-row:hover td:first-child {
    background: rgba(99,102,241,0.04) !important;
  }

  /* ── Chips ── */
  .sbt-chip {
    display: flex;
    align-items: center;
    gap: 5px;
    padding: 4px 10px;
    border-radius: 100px;
    flex-shrink: 0;
    white-space: nowrap;
    transition: transform 0.1s, box-shadow 0.1s;
    cursor: default;
    border: 1px solid transparent;
  }
  .sbt-chip:hover {
    transform: translateY(-1px);
    box-shadow: var(--shadow-sm);
  }

  /* ── Loading spinner ── */
  @keyframes sbt-spin {
    to { transform: rotate(360deg); }
  }
  @keyframes sbt-fade-in {
    from { opacity: 0; transform: translateY(4px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  .sbt-animate {
    animation: sbt-fade-in 0.3s ease forwards;
  }

  /* ── Workload bar fill glow ── */
  .sbt-bar-fill {
    height: 100%;
    border-radius: 100px;
    position: relative;
    transition: width 0.6s cubic-bezier(0.22, 1, 0.36, 1);
  }

  /* ── Prod bar disabled pulse ── */
  .sbt-prod-disabled {
    opacity: 0.45;
  }

  /* ── Score pill ── */
  .sbt-score-pill {
    font-size: 10px;
    font-weight: 600;
    padding: 2px 6px;
    border-radius: 100px;
    flex-shrink: 0;
    letter-spacing: 0.02em;
  }

  /* ── Coming soon badge ── */
  .sbt-soon {
    font-size: 9px;
    font-weight: 600;
    padding: 1px 5px;
    border-radius: 4px;
    background: #f0f2f6;
    color: #9ba3b5;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    border: 1px solid #e4e7ed;
    flex-shrink: 0;
  }

  /* ── Section label ── */
  .sbt-bar-label {
    font-size: 9px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.07em;
    color: var(--text-tertiary);
    margin-bottom: 3px;
  }

  /* ── Sticky header ── */
  .sbt-th {
    position: sticky;
    top: 0;
    z-index: 10;
    background: #f8f9fc;
    font-size: 10.5px;
    font-weight: 600;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: var(--text-tertiary);
  }

  /* ── Footer total ── */
  .sbt-tfoot td {
    background: #f8f9fc;
    border-top: 1.5px solid var(--border);
  }

  /* ── Cell count ── */
  .sbt-count {
    font-family: var(--font-mono);
    font-size: 13px;
    font-weight: 500;
  }
  .sbt-pct-small {
    font-family: var(--font-mono);
    font-size: 10.5px;
    color: var(--text-tertiary);
  }
`;

function WorkloadLegend() {
  return (
    <div style={{
      display: "flex",
      alignItems: "center",
      gap: "4px",
      flexWrap: "wrap",
    }}>
      <span style={{
        fontSize: "10.5px",
        color: "#9ba3b5",
        fontWeight: 600,
        letterSpacing: "0.05em",
        textTransform: "uppercase",
        marginRight: "4px",
        whiteSpace: "nowrap",
      }}>
        Carga
      </span>
      {LEGEND_ITEMS.map(it => (
        <div key={it.label} style={{
          display: "flex", alignItems: "center", gap: "4px",
          padding: "3px 8px",
          borderRadius: "100px",
          background: it.track,
          border: `1px solid ${it.fill}22`,
        }}>
          <div style={{ width: 6, height: 6, borderRadius: "50%", background: it.fill }}/>
          <span style={{ fontSize: "10.5px", fontWeight: 600, color: it.text }}>{it.label}</span>
          <span style={{ fontSize: "10px", color: it.text, opacity: 0.6 }}>{it.range}</span>
        </div>
      ))}
    </div>
  );
}

export default function ServicesByTechnicianDetailedChart() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  const currentDate = new Date();
  const [selectedYear,  setSelectedYear]  = useState(currentDate.getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(currentDate.getMonth() + 1);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      const res = await dashboardService.getServicesByTechnicianAndType(selectedYear, selectedMonth);
      setData(res.data || []);
      setLoading(false);
    };
    load();
  }, [selectedYear, selectedMonth]);

  const typeTotals = useMemo(() => {
    const map = new Map();
    data.forEach(t => {
      t.servicesByType?.forEach(s => {
        const key = String(s.type);
        map.set(key, (map.get(key) || 0) + (s.count || 0));
      });
    });
    return map;
  }, [data]);

  const allTypes = useMemo(() =>
    Array.from(typeTotals.entries()).sort((a, b) => b[1] - a[1]).map(e => e[0]),
    [typeTotals]
  );

  const visibleTypes = allTypes.slice(0, MAX_VISIBLE_COLUMNS);
  const extraTypes   = allTypes.slice(MAX_VISIBLE_COLUMNS);
  const totalGlobal  = Array.from(typeTotals.values()).reduce((a, b) => a + b, 0);

  const getCount    = (tech, type) => tech.servicesByType?.find(s => String(s.type) === type)?.count || 0;
  const colorOf     = (i) => TYPE_COLORS[i % TYPE_COLORS.length];
  const typeGlobPct = (type) => totalGlobal > 0 ? (typeTotals.get(type) || 0) / totalGlobal * 100 : 0;

  const TH = {
    className: "sbt-th",
    style: { padding: "10px 6px", borderBottom: "1.5px solid #e4e7ed" },
  };

  return (
    <div className="sbt-root" style={{
      height: "100%",
      display: "flex",
      flexDirection: "column",
      background: "#ffffff",
      borderRadius: "16px",
      border: "1px solid #e8e8e6",
      overflow: "hidden",
      fontSize: "13px",
      color: "#111110",
    }}>
      <style>{GLOBAL_STYLES}</style>

      {/* ══ HEADER ══ */}
      <div style={{
        padding: "16px 20px 14px",
        borderBottom: "1px solid #f0f0ee",
        flexShrink: 0,
        background: "#ffffff",
      }}>
        {/* Fila 1 */}
        <div style={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: "16px",
          marginBottom: "12px",
        }}>
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "3px" }}>
              <span style={{ fontSize: "13px", fontWeight: 600, color: "#111110", letterSpacing: "-0.02em" }}>
                Distribución por técnico
              </span>
            </div>
            <div style={{ fontSize: "12px", color: "#9ba3b5" }}>
              {!loading
                ? `${data.length} técnicos · ${totalGlobal.toLocaleString()} servicios · ${MONTHS[selectedMonth - 1]} ${selectedYear}`
                : "Cargando datos…"
              }
            </div>
          </div>

          {/* Selectores */}
          <div style={{ display: "flex", alignItems: "center", gap: "8px", flexShrink: 0 }}>
            {[
              { value: selectedMonth, onChange: setSelectedMonth, options: MONTHS.map((m,i) => ({ label: m, value: i + 1 })) },
              { value: selectedYear,  onChange: setSelectedYear,  options: YEARS.map(y => ({ label: String(y), value: y })) },
            ].map((sel, idx) => (
              <div key={idx} style={{ position: "relative", display: "inline-flex", alignItems: "center" }}>
                <select
                  className="sbt-select"
                  value={sel.value}
                  onChange={e => sel.onChange(Number(e.target.value))}
                >
                  {sel.options.map(o => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
                <svg
                  style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", pointerEvents: "none" }}
                  width="10" height="6" viewBox="0 0 10 6" fill="none"
                >
                  <path d="M1 1L5 5L9 1" stroke="#9ba3b5" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
            ))}
          </div>
        </div>

        {/* Fila 2: leyenda */}
        <WorkloadLegend />
      </div>

      {/* ══ CHIPS DE TIPOS ══ */}
      {!loading && visibleTypes.length > 0 && (
        <div className="sbt-scroll" style={{
          display: "flex",
          gap: "6px",
          padding: "10px 20px",
          borderBottom: "1.5px solid #f0f2f6",
          overflowX: "auto",
          flexShrink: 0,
          background: "#fafbfd",
        }}>
          {visibleTypes.map((type, i) => {
            const count = typeTotals.get(type) || 0;
            const pct   = typeGlobPct(type).toFixed(1);
            const color = colorOf(i);
            const rgb   = hexToRgb(color);
            return (
              <div
                key={type}
                className="sbt-chip"
                style={{
                  background: `rgba(${rgb},0.07)`,
                  border: `1px solid rgba(${rgb},0.18)`,
                }}
              >
                <div style={{ width: 7, height: 7, borderRadius: "50%", background: color, flexShrink: 0 }}/>
                <span style={{ fontSize: "11px", fontWeight: 600, color, letterSpacing: "0.03em" }}>
                  {type.substring(0, 8).toUpperCase()}
                </span>
                <span style={{ fontSize: "11px", fontWeight: 700, color: "#374151", fontFamily: "'DM Mono', monospace" }}>
                  {count}
                </span>
                <span style={{ fontSize: "11px", color: "#9ba3b5", fontFamily: "'DM Mono', monospace" }}>
                  {pct}%
                </span>
              </div>
            );
          })}
          {extraTypes.length > 0 && (
            <div style={{
              padding: "4px 12px",
              borderRadius: "100px",
              background: "#f2f4f8",
              border: "1px solid #e4e7ed",
              fontSize: "11px",
              color: "#9ba3b5",
              fontWeight: 600,
              flexShrink: 0,
              whiteSpace: "nowrap",
              display: "flex",
              alignItems: "center",
            }}>
              +{extraTypes.length} más
            </div>
          )}
        </div>
      )}

      {/* ══ TABLA ══ */}
      <div className="sbt-scroll" style={{ flex: 1, overflow: "auto", minHeight: 0 }}>
        {loading ? (
          <div style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            height: "200px",
            gap: "12px",
            color: "#9ba3b5",
          }}>
            <svg
              width="20" height="20" viewBox="0 0 20 20"
              style={{ animation: "sbt-spin 0.8s linear infinite" }}
            >
              <circle cx="10" cy="10" r="8" fill="none" stroke="#e4e7ed" strokeWidth="2.5"/>
              <path d="M10 2a8 8 0 0 1 8 8" fill="none" stroke="#6366f1" strokeWidth="2.5" strokeLinecap="round"/>
            </svg>
            <span style={{ fontSize: "13px", fontWeight: 500 }}>Cargando datos…</span>
          </div>
        ) : (
          <table style={{
            width: "100%",
            borderCollapse: "collapse",
            tableLayout: "fixed",
          }}>
            <thead>
              <tr>
                {/* Técnico */}
                <th
                  {...TH}
                  style={{ ...TH.style, padding: "10px 12px 10px 20px", textAlign: "left", width: "28%" }}
                >
                  Técnico
                </th>

                {/* Tipos visibles */}
                {visibleTypes.map((type, i) => (
                  <th
                    key={type}
                    title={type}
                    {...TH}
                    style={{
                      ...TH.style,
                      borderBottom: `2px solid ${colorOf(i)}`,
                      textAlign: "center",
                      color: colorOf(i),
                      width: `${54 / MAX_VISIBLE_COLUMNS}%`,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {type.substring(0, 6).toUpperCase()}
                  </th>
                ))}

                {/* Extra */}
                {extraTypes.length > 0 && (
                  <th
                    {...TH}
                    style={{ ...TH.style, textAlign: "center", color: "#9ba3b5", width: "4%" }}
                  >
                    +{extraTypes.length}
                  </th>
                )}

                {/* Total */}
                <th
                  {...TH}
                  style={{ ...TH.style, padding: "10px 20px 10px 4px", textAlign: "right", width: "12%" }}
                >
                  Total
                </th>
              </tr>
            </thead>

            <tbody>
              {data.map((tech, rowIdx) => {
                const score = calcWorkload(tech, data, typeTotals, totalGlobal);
                const wm    = workloadMeta(score);
                const techPct = totalGlobal > 0 ? ((tech.totalServices / totalGlobal) * 100).toFixed(0) : 0;

                return (
                  <tr key={rowIdx} className="sbt-row sbt-animate" style={{ animationDelay: `${rowIdx * 30}ms` }}>

                    {/* ── NOMBRE + BARRAS ── */}
                    <td style={{
                      padding: "9px 12px 9px 20px",
                      borderBottom: "1px solid #f0f2f6",
                      verticalAlign: "middle",
                    }}>
                      {/* Nombre */}
                      <div style={{
                        fontWeight: 500,
                        color: "#0f1117",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        marginBottom: "7px",
                        fontSize: "13px",
                      }}>
                        {tech.technicianName}
                      </div>

                      {/* Barras */}
                      <div style={{ display: "flex", gap: "10px", alignItems: "stretch" }}>

                        {/* CARGA */}
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div className="sbt-bar-label">Carga</div>
                          <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                            <div style={{
                              flex: 1,
                              height: "5px",
                              background: wm.track,
                              borderRadius: "100px",
                              overflow: "hidden",
                            }}>
                              <div className="sbt-bar-fill" style={{
                                width: `${score}%`,
                                background: `linear-gradient(90deg, ${wm.fill}cc, ${wm.fill})`,
                              }}/>
                            </div>
                            <span style={{
                              fontSize: "10px",
                              fontWeight: 600,
                              color: wm.text,
                              minWidth: "26px",
                              textAlign: "right",
                              fontFamily: "'DM Mono', monospace",
                            }}>
                              {score}%
                            </span>
                            <span className="sbt-score-pill" style={{
                              background: wm.track,
                              color: wm.text,
                            }}>
                              {wm.label}
                            </span>
                          </div>
                        </div>
                      </div>
                    </td>

                    {/* ── CELDAS TIPO ── */}
                    {visibleTypes.map((type, colIdx) => {
                      const count     = getCount(tech, type);
                      const typeTotal = typeTotals.get(type) || 1;
                      const pct       = count / typeTotal;
                      const color     = colorOf(colIdx);
                      const alpha     = count ? Math.max(0.06, pct * 0.45) : 0;
                      const bgRgb     = hexToRgb(color);

                      return (
                        <td
                          key={type}
                          title={count ? `${type}: ${count} servicios · ${(pct * 100).toFixed(0)}% del tipo` : undefined}
                          style={{
                            padding: "9px 4px",
                            borderBottom: "1px solid #f0f2f6",
                            textAlign: "center",
                            verticalAlign: "middle",
                            background: count ? `rgba(${bgRgb},${alpha})` : "transparent",
                            transition: "background 0.15s",
                          }}
                        >
                          {count ? (
                            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", lineHeight: 1.3 }}>
                              <span className="sbt-count" style={{ color, fontWeight: 600 }}>{count}</span>
                              <span className="sbt-pct-small">{(pct * 100).toFixed(0)}%</span>
                            </div>
                          ) : (
                            <span style={{ color: "#e4e7ed", fontSize: "12px" }}>—</span>
                          )}
                        </td>
                      );
                    })}

                    {/* Extra tipos */}
                    {extraTypes.length > 0 && (
                      <td style={{
                        padding: "9px 4px",
                        borderBottom: "1px solid #f0f2f6",
                        textAlign: "center",
                        verticalAlign: "middle",
                        color: "#9ba3b5",
                        fontWeight: 500,
                        background: "transparent",
                        fontSize: "12px",
                      }}>
                        {extraTypes.reduce((sum, t) => sum + getCount(tech, t), 0) || "—"}
                      </td>
                    )}

                    {/* Total */}
                    <td style={{
                      padding: "9px 20px 9px 4px",
                      borderBottom: "1px solid #f0f2f6",
                      textAlign: "right",
                      verticalAlign: "middle",
                    }}>
                      <div className="sbt-count" style={{ fontWeight: 600, color: "#0f1117", fontSize: "13px" }}>
                        {tech.totalServices?.toLocaleString()}
                      </div>
                      <div className="sbt-pct-small">{techPct}%</div>
                    </td>
                  </tr>
                );
              })}
            </tbody>

            {/* ══ FOOTER ══ */}
            {data.length > 0 && (
              <tfoot>
                <tr className="sbt-tfoot">
                  <td style={{ padding: "10px 12px 10px 20px" }}>
                    <span style={{
                      fontSize: "10.5px",
                      fontWeight: 700,
                      letterSpacing: "0.07em",
                      textTransform: "uppercase",
                      color: "#5a6175",
                    }}>
                      Total equipo
                    </span>
                  </td>

                  {visibleTypes.map((type, i) => {
                    const count = typeTotals.get(type) || 0;
                    const pct   = typeGlobPct(type);
                    return (
                      <td key={type} style={{ padding: "10px 4px", textAlign: "center" }}>
                        <div className="sbt-count" style={{ fontWeight: 600, color: colorOf(i), fontSize: "13px" }}>
                          {count}
                        </div>
                        <div className="sbt-pct-small">{pct.toFixed(0)}%</div>
                      </td>
                    );
                  })}

                  {extraTypes.length > 0 && (
                    <td style={{ padding: "10px 4px", textAlign: "center" }}>
                      <span className="sbt-count" style={{ fontWeight: 600, color: "#9ba3b5" }}>
                        {extraTypes.reduce((sum, t) => sum + (typeTotals.get(t) || 0), 0)}
                      </span>
                    </td>
                  )}

                  <td style={{ padding: "10px 20px 10px 4px", textAlign: "right" }}>
                    <div className="sbt-count" style={{ fontWeight: 700, color: "#0f1117", fontSize: "14px" }}>
                      {totalGlobal.toLocaleString()}
                    </div>
                    <div className="sbt-pct-small">100%</div>
                  </td>
                </tr>
              </tfoot>
            )}
          </table>
        )}
      </div>
    </div>
  );
}